/**
 * Registrant export — maps event_attendees rows to a flat spreadsheet shape for
 * the "Export Registrants" button on the event Attendees tab.
 *
 * Rewritten 2026-09-09. It previously built every count straight off the raw
 * `party_*` / `attended_*` columns with `?? 0`, applying NONE of the canonical
 * resolver's rules, so the file contradicted the screen it was exported from:
 *   - no `GREATEST(pm + pnm, 1)` floor, so a companion/guest row whose count columns
 *     are all 0 (the walk-in write path leaves them at their defaults, and the
 *     sponsor's row does not carry them either) exported as 0 registered / 0 attended
 *     while showing Status `attended` with a check-in timestamp — a person the
 *     canonical resolver counts as 1 body;
 *   - no "verified where known, else the registered headcount of the checked-in
 *     party" fallback, so every advance registrant exported a BLANK Attended (those
 *     columns are NULL until staff confirm a party headcount at check-in).
 *
 * It now routes through `utils/eventAttendance`, the same arithmetic
 * `get_event_attendance_summary` uses, so the file's column totals reconcile to the
 * Overview and Attendees numbers. That requires the row's canonical COHORT
 * (from `get_event_attendance_roster`); without it the Attended columns cannot be
 * decided, so the caller must supply it — see `registrantRowToExport`.
 *
 * Header set and order are UNCHANGED; only the values are now canonical.
 */
import {
  type AttendanceCohort,
  canonicalRowAttendance,
  canonicalRowRegistered,
  memberStatusLabel,
} from "../../utils/eventAttendance";

export interface RegistrantExportInput {
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  email?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  attendee_type?: string | null;
  /**
   * The embedded contact row (getEventAttendees selects `contacts!contact_id(*)`).
   * Only the membership evidence is read: to decide which bucket a floor-supplied
   * body lands in (never a total — see isMemberForHeadcount()), and to derive the
   * "Member Status" column (see memberStatusLabel()).
   */
  contacts?: {
    contact_type?: string | null;
    membership_id?: string | null;
    has_active_subscription?: boolean | null;
    member_status?: string | null;
    /** Zuora PolicyReasonForCancellation. Drives the Deceased rung. Already fetched:
     *  the embed is `contacts!contact_id(*)` (events.service.ts:419), so this is a
     *  read of a column that is present, not a new select. */
    cancellation_reason?: string | null;
  } | null;
  party_member_count?: number | null;
  party_nonmember_count?: number | null;
  attended_member_count?: number | null;
  attended_nonmember_count?: number | null;
  attendance_status?: string | null;
  checked_in_at?: string | null;
  registration_source?: string | null;
  registration_date?: string | null;
  created_at?: string | null;
}

// Column order = header order in the exported file.
export const REGISTRANT_EXPORT_COLUMNS = [
  "Name",
  "Email",
  "Phone",
  "Registered Members",
  "Registered Non-Members",
  "Registered Total",
  "Attended Members",
  "Attended Non-Members",
  "Attended Total",
  "Status",
  "Checked In At",
  "Registration Source",
  "Registered At",
  // APPENDED, deliberately last. Operators paste this file into existing spreadsheets,
  // so inserting a column mid-file would shift every later column's index and break
  // formulas keyed to a letter. Appending leaves all 13 existing headers at both their
  // original name and their original position.
  "Member Status",
] as const;

/**
 * @param a      the attendee row
 * @param cohort the row's canonical cohort from `get_event_attendance_roster`. Pass
 *               `null`/`undefined` only when the roster genuinely could not be loaded:
 *               the Attended columns then export blank rather than a number that would
 *               not reconcile to the resolver.
 */
export function registrantRowToExport(
  a: RegistrantExportInput,
  cohort?: AttendanceCohort | null,
): Record<string, unknown> {
  const name =
    (a.display_name && a.display_name.trim()) ||
    [a.first_name, a.last_name].filter(Boolean).join(" ").trim() ||
    "";

  // Membership evidence, flattened out of the embedded contact, for the member /
  // non-member split AND the "Member Status" column. The owner's "membership id wins"
  // cascade lives in isMemberForHeadcount() and memberStatusLabel() wraps it; this only
  // hands them what they need. Totals do not depend on any of it.
  const shaped = {
    ...a,
    contact_type: a.contacts?.contact_type ?? null,
    membership_id: a.contacts?.membership_id ?? null,
    has_active_subscription: a.contacts?.has_active_subscription ?? null,
    member_status: a.contacts?.member_status ?? null,
    // Feeds the Deceased rung. Without it the rung can never fire and the label
    // silently falls through to the subscription-derived states.
    cancellation_reason: a.contacts?.cancellation_reason ?? null,
  };

  // Registered: canonical (floored + split-attributed) so a zero-count companion row
  // still exports as the one body the resolver counts.
  const reg = canonicalRowRegistered(shaped);

  // Attended: canonical bodies present. Blank (not 0) when the cohort is unknown, so a
  // failed roster load reads as "not computed" instead of "nobody came".
  const cohortKnown = cohort !== null && cohort !== undefined;
  const att = canonicalRowAttendance(shaped, cohort);

  return {
    Name: name,
    Email: a.contact_email || a.email || "",
    Phone: a.phone || "",
    "Registered Members": reg.memberHeadcount,
    "Registered Non-Members": reg.nonmemberHeadcount,
    "Registered Total": reg.headcount,
    "Attended Members": cohortKnown ? att.memberHeadcount : "",
    "Attended Non-Members": cohortKnown ? att.nonmemberHeadcount : "",
    "Attended Total": cohortKnown ? att.headcount : "",
    Status: a.attendance_status || "",
    "Checked In At": a.checked_in_at || "",
    "Registration Source": a.registration_source || "",
    "Registered At": a.registration_date || a.created_at || "",
    // What the person IS. Five states, "Unknown" whenever the evidence contradicts
    // itself or there is no linked contact at all — never a silent default into one of
    // the others. A falsy `a.contacts` is the contactless walk-in case. "Deceased" is
    // reported separately from "Former Member (Cancelled)" and outranks it.
    "Member Status": memberStatusLabel(shaped, Boolean(a.contacts)),
  };
}
